import React, { useEffect, useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/toast';
import {
  Phone,
  PhoneCall,
  Search,
  Trash2,
  Download,
  RefreshCw,
  User,
  AlertTriangle,
  Building,
  Droplet,
  Shield,
  MessageCircle,
  Copy,
  ChevronLeft,
  ChevronRight,
  Globe,
  Check
} from 'lucide-react';

const ACTION_CONFIG = {
  call_click: {
    label: 'Call Clicked',
    icon: PhoneCall,
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
  },
  whatsapp_click: {
    label: 'WhatsApp Chat',
    icon: MessageCircle,
    badge: 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300 border-green-200 dark:border-green-800'
  },
  copy_number: {
    label: 'Copied Number',
    icon: Copy,
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800'
  },
  view: {
    label: 'Viewed',
    icon: Phone,
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800'
  }
};

const TYPE_CONFIG = {
  officer: {
    label: 'Officer',
    icon: Shield,
    badge: 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800'
  },
  business: {
    label: 'Business',
    icon: Building,
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800'
  },
  blood_donor: {
    label: 'Blood Donor',
    icon: Droplet,
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200 dark:border-rose-800'
  },
  emergency: {
    label: 'Emergency',
    icon: AlertTriangle,
    badge: 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-200 dark:border-red-800'
  },
  other: {
    label: 'Other',
    icon: Globe,
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700'
  }
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInSeconds / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d ago`;

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const AdminPhoneLogsContent = () => {
  const { isCollapsed } = useSidebar();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLogs: 0,
    uniqueUsersCount: 0,
    officerLogs: 0,
    businessLogs: 0,
    bloodDonorLogs: 0
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 25
  });

  const [search, setSearch] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);

  // Clear modal confirmation state
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchLogs = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: targetPage,
        limit: 25
      });

      if (search.trim()) params.set('search', search.trim());
      if (targetType && targetType !== 'all') params.set('targetType', targetType);
      if (actionFilter && actionFilter !== 'all') params.set('action', actionFilter);

      const res = await api.get(`/phone-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { total: 0, page: 1, pages: 1, limit: 25 });
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error('Error fetching phone logs:', err);
      showError(err.response?.data?.message || 'Failed to load phone audit logs');
    } finally {
      setLoading(false);
    }
  }, [search, targetType, actionFilter]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const handleDeleteLog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this log entry?')) return;
    try {
      await api.delete(`/phone-logs/${id}`);
      showSuccess('Log entry deleted');
      setLogs((prev) => prev.filter((item) => item._id !== id));
      setStats((prev) => ({ ...prev, totalLogs: Math.max(0, prev.totalLogs - 1) }));
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete log entry');
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await api.delete('/phone-logs/clear/all');
      showSuccess('All phone logs cleared successfully');
      setShowClearModal(false);
      fetchLogs(1);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to clear phone logs');
    } finally {
      setClearing(false);
    }
  };

  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExportCSV = () => {
    if (!logs.length) {
      showError('No logs available to export');
      return;
    }

    const headers = [
      'Timestamp',
      'User Type',
      'User Name',
      'User Email',
      'User Phone',
      'Target Type',
      'Target Name',
      'Target Category',
      'Viewed Phone',
      'Action',
      'IP Address',
      'Page URL'
    ];

    const rows = logs.map((l) => [
      new Date(l.createdAt).toLocaleString('en-IN'),
      l.userId ? 'Registered User' : 'Guest',
      l.userName || '',
      l.userEmail || '',
      l.userPhone || '',
      l.targetType || '',
      `"${(l.targetName || '').replace(/"/g, '""')}"`,
      `"${(l.targetCategory || '').replace(/"/g, '""')}"`,
      l.viewedPhone || '',
      l.action || '',
      l.ipAddress || '',
      l.pageUrl || ''
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `phone_view_logs_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSuccess('Logs exported to CSV successfully');
  };

  return (
    <>
      <SEO
        title="Admin - Phone Audit Logs | Digital Fatehabad"
        description="Monitor which user is viewing and calling phone numbers across the project"
        noindex
      />

      <main
        className={`flex-1 transition-all duration-300 min-h-screen bg-gray-50/60 dark:bg-gray-950 ${
          isCollapsed ? 'ml-16' : 'ml-64'
        }`}
      >
        <div className="container-custom py-6 md:py-10 max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-primary-600 to-indigo-600 text-white flex items-center justify-center shadow-lg shadow-primary-500/20">
                <PhoneCall className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
                  Phone View Logs
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary-100 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 font-semibold border border-primary-200 dark:border-primary-800">
                    Audit Tracking
                  </span>
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Track kon sa user kon sa phone number dekh rha hai, call kar rha hai ya copy kar rha hai.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                onClick={() => fetchLogs(pagination.page)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
                title="Refresh Logs"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              <button
                onClick={handleExportCSV}
                disabled={logs.length === 0}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
                title="Export current page to CSV"
              >
                <Download className="w-4 h-4 text-emerald-600" />
                Export CSV
              </button>

              <button
                onClick={() => setShowClearModal(true)}
                disabled={stats.totalLogs === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 text-sm font-semibold transition-colors disabled:opacity-50"
                title="Clear all recorded logs"
              >
                <Trash2 className="w-4 h-4" />
                Clear All Logs
              </button>
            </div>
          </div>

          {/* Stats Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5 mb-8">
            {/* Total Calls & Views */}
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                <PhoneCall className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Total Views/Calls</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalLogs.toLocaleString()}</p>
              </div>
            </div>

            {/* Unique Registered Users */}
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Registered Users</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.uniqueUsersCount.toLocaleString()}</p>
              </div>
            </div>

            {/* Officers Inquiries */}
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Officer Calls</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.officerLogs.toLocaleString()}</p>
              </div>
            </div>

            {/* Business Inquiries */}
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Business Calls</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.businessLogs.toLocaleString()}</p>
              </div>
            </div>

            {/* Blood Donors Inquiries */}
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5 col-span-2 sm:col-span-1">
              <div className="w-11 h-11 rounded-xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
                <Droplet className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Blood Donor Calls</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.bloodDonorLogs.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm mb-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5">
              {/* Search */}
              <div className="md:col-span-6 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search user name, email, phone, or target contact..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                />
              </div>

              {/* Target Type Filter */}
              <div className="md:col-span-3">
                <select
                  value={targetType}
                  onChange={(e) => setTargetType(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                >
                  <option value="all">All Entity Types</option>
                  <option value="officer">Officers Directory</option>
                  <option value="business">Businesses</option>
                  <option value="blood_donor">Blood Donors</option>
                  <option value="emergency">Emergency Helplines</option>
                </select>
              </div>

              {/* Action Filter */}
              <div className="md:col-span-3">
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                >
                  <option value="all">All Actions</option>
                  <option value="call_click">Direct Call Click</option>
                  <option value="whatsapp_click">WhatsApp Chat Click</option>
                  <option value="copy_number">Copied Number</option>
                  <option value="view">Viewed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-16 flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Loading audit logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4 text-gray-400">
                  <PhoneCall className="w-8 h-8 opacity-40" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  No Phone View Logs Found
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  {search || targetType !== 'all' || actionFilter !== 'all'
                    ? 'No events match your current search and filter settings. Try clearing filters.'
                    : 'Whenever someone views, calls or copies an officer, business or donor number, it will automatically show here in real-time.'}
                </p>
                {(search || targetType !== 'all' || actionFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearch('');
                      setTargetType('all');
                      setActionFilter('all');
                    }}
                    className="mt-4 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-300 text-xs font-semibold"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <th className="py-3.5 px-4">Time</th>
                      <th className="py-3.5 px-4">User Details</th>
                      <th className="py-3.5 px-4">Target Contact</th>
                      <th className="py-3.5 px-4">Phone Viewed</th>
                      <th className="py-3.5 px-4">Action Taken</th>
                      <th className="py-3.5 px-4">IP / Page</th>
                      <th className="py-3.5 px-4 text-right">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-xs">
                    {logs.map((log) => {
                      const actionInfo = ACTION_CONFIG[log.action] || {
                        label: log.action,
                        icon: Phone,
                        badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      };
                      const ActionIcon = actionInfo.icon;

                      const typeInfo = TYPE_CONFIG[log.targetType] || TYPE_CONFIG.other;
                      const TypeIcon = typeInfo.icon;

                      const isGuest = !log.userId;

                      return (
                        <tr
                          key={log._id}
                          className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          {/* Time */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <span className="font-semibold text-gray-800 dark:text-gray-200 block">
                              {formatTimeAgo(log.createdAt)}
                            </span>
                            <span className="text-[10px] text-gray-400 block" title={new Date(log.createdAt).toISOString()}>
                              {new Date(log.createdAt).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </td>

                          {/* User */}
                          <td className="py-3.5 px-4 min-w-[200px]">
                            <div className="flex items-start gap-2.5">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                                  isGuest
                                    ? 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                                    : 'bg-primary-100 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300'
                                }`}
                              >
                                {isGuest ? '?' : (log.userName || 'U')[0].toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-bold text-gray-900 dark:text-white truncate">
                                    {log.userName || 'Guest User'}
                                  </span>
                                  {isGuest ? (
                                    <span className="px-1.5 py-0.2 text-[9px] font-semibold rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                      Guest
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.2 text-[9px] font-semibold rounded bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">
                                      User
                                    </span>
                                  )}
                                </div>
                                {log.userEmail && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                    {log.userEmail}
                                  </p>
                                )}
                                {log.userPhone && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                    📞 {log.userPhone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Target Contact */}
                          <td className="py-3.5 px-4 min-w-[210px]">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${typeInfo.badge}`}
                                >
                                  <TypeIcon className="w-3 hand-3" />
                                  {typeInfo.label}
                                </span>
                              </div>
                              <span className="font-bold text-gray-900 dark:text-white line-clamp-1">
                                {log.targetName}
                              </span>
                              {log.targetCategory && (
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1">
                                  {log.targetCategory}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Phone Viewed */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800/80 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700">
                              <span className="font-mono font-bold text-gray-900 dark:text-white">
                                {log.viewedPhone}
                              </span>
                              <button
                                onClick={() => handleCopy(log.viewedPhone, log._id)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                title="Copy number"
                              >
                                {copiedId === log._id ? (
                                  <Check className="w-3.5 h-3.5 text-green-500" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </td>

                          {/* Action */}
                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${actionInfo.badge}`}
                            >
                              <ActionIcon className="w-3.5 h-3.5" />
                              {actionInfo.label}
                            </span>
                          </td>

                          {/* IP / Page */}
                          <td className="py-3.5 px-4 max-w-[180px]">
                            <p className="font-mono text-[11px] text-gray-700 dark:text-gray-300 truncate" title={log.ipAddress}>
                              IP: {log.ipAddress || 'unknown'}
                            </p>
                            {log.pageUrl && (
                              <p className="text-[10px] text-gray-400 truncate" title={log.pageUrl}>
                                {log.pageUrl}
                              </p>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteLog(log._id)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                              title="Delete this record"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/40 dark:bg-gray-800/20 text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  <strong className="text-gray-900 dark:text-white font-bold">{pagination.total}</strong> logs
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => fetchLogs(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="px-3 py-1 font-semibold text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.pages}
                  </span>

                  <button
                    onClick={() => fetchLogs(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages || loading}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Clear All Confirmation Modal */}
      {showClearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-800 animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 rounded-2xl bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Clear All Phone View Logs?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              This action will permanently delete all recorded phone audit records from the database. This cannot be undone.
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowClearModal(false)}
                disabled={clearing}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={clearing}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow-md transition-all disabled:opacity-50"
              >
                {clearing ? 'Clearing...' : 'Yes, Clear All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const AdminPhoneLogs = () => (
  <AdminLayout>
    <AdminPhoneLogsContent />
  </AdminLayout>
);

export default AdminPhoneLogs;
